import {
  CommonOrderDetails,
  OrderData,
  ApprovalDetails,
  CFROIResponse,
  SKUItem,
  SkuPaymentSeries,
} from "../types/api";

/**
 * Maps OrderData and CFROIResponse to CommonOrderDetails
 */
export function mapOrderDataToCommon(
  orderData: OrderData,
  cfroiData?: CFROIResponse
): CommonOrderDetails {
  return {
    orderNumber: orderData.orderNumber,
    supplier: cfroiData?.supplier ?? orderData.supplier,
    erpPaymentTerms: cfroiData?.erpPaymentTerms ?? orderData.erpPaymentTerms,
    purchasingEntity: orderData.purchasingEntity,
    purchasingManager: orderData.purchasingManager,
    paymentTerms: orderData.paymentTerms,
    portfolioCfroi: orderData.cfroi,
    skus: orderData.skus,
    skuPaymentSeries: cfroiData?.skuPaymentSeries,
  };
}

/**
 * Maps ApprovalDetails to CommonOrderDetails
 */
export function mapApprovalDetailsToCommon(
  approvalDetails: ApprovalDetails,
  paymentTerms: number = 30
): CommonOrderDetails {
  // Use the stored payment terms from the approval data first, then the parameter fallback
  const effectivePaymentTerms = approvalDetails.paymentTerms ?? paymentTerms;
  // Convert OrderPositions to SKUItems with enhanced data from SkuPaymentSeries
  const skus: SKUItem[] =
    approvalDetails.orderPositions?.map((pos) => {
      // Find corresponding payment series data for enhanced SKU information
      const skuPaymentData = approvalDetails.skuPaymentSeries?.find(
        (skuData) => skuData.position.sku === pos.sku
      );

      // Use item name from payment series if available, otherwise fall back to order position
      const itemName =
        skuPaymentData?.position.itemName || pos.itemName || `Item ${pos.sku}`;

      return {
        skuId: pos.sku,
        itemName: itemName,
        purchasingVolume: pos.price * pos.quantity,
        amount: pos.quantity,
        currentStock: skuPaymentData?.currentStock || 0,
        cfroi: skuPaymentData?.cfroi || 0,
        npv: skuPaymentData?.npv || 0,
        dailySalesRate: skuPaymentData?.dailySalesRate,
        hasSalesData: skuPaymentData?.hasSalesData ?? false,
      };
    }) || [];

  // If no order positions but we have payment series data, create SKUs from payment series
  if (
    (!approvalDetails.orderPositions ||
      approvalDetails.orderPositions.length === 0) &&
    approvalDetails.skuPaymentSeries &&
    approvalDetails.skuPaymentSeries.length > 0
  ) {
    const skusFromPaymentSeries: SKUItem[] =
      approvalDetails.skuPaymentSeries.map((paymentData) => ({
        skuId: paymentData.position.sku,
        itemName:
          paymentData.position.itemName || `Item ${paymentData.position.sku}`,
        purchasingVolume:
          paymentData.position.price * paymentData.position.quantity,
        amount: paymentData.position.quantity,
        currentStock: paymentData.currentStock || 0,
        cfroi: paymentData.cfroi || 0,
        npv: paymentData.npv || 0,
        dailySalesRate: paymentData.dailySalesRate,
        hasSalesData: paymentData.hasSalesData ?? false,
      }));

    return {
      orderNumber: approvalDetails.orderNumber,
      purchasingEntity: "Caseking", // Default value
      purchasingManager: "TBD", // Not available in approval details
      supplier: approvalDetails.supplier,
      erpPaymentTerms: approvalDetails.erpPaymentTerms,
      paymentTerms: effectivePaymentTerms,
      portfolioCfroi: approvalDetails.portfolioCfroi || 0,
      totalInvestment: approvalDetails.totalInvestment,
      skus: skusFromPaymentSeries,
      skuPaymentSeries: approvalDetails.skuPaymentSeries,
    };
  }

  return {
    orderNumber: approvalDetails.orderNumber,
    purchasingEntity: "Caseking", // Default value
    purchasingManager: "TBD", // Not available in approval details
    supplier: approvalDetails.supplier,
    erpPaymentTerms: approvalDetails.erpPaymentTerms,
    paymentTerms: effectivePaymentTerms,
    portfolioCfroi: approvalDetails.portfolioCfroi || 0,
    totalInvestment: approvalDetails.totalInvestment,
    skus,
    skuPaymentSeries: approvalDetails.skuPaymentSeries,
  };
}

/**
 * Creates a CFROIResponse-like object from ApprovalDetails for chart compatibility
 */
export function createCFROIResponseFromApproval(
  approvalDetails: ApprovalDetails
): CFROIResponse | undefined {
  if (!approvalDetails.skuPaymentSeries) {
    return undefined;
  }

  return {
    requestId: approvalDetails.requestId,
    orderNumber: approvalDetails.orderNumber,
    success: true,
    skuPaymentSeries: approvalDetails.skuPaymentSeries,
    portfolioCfroi: approvalDetails.portfolioCfroi,
    calculationTime: approvalDetails.createdAt,
    processingTimeMs: 0, // Not available
    purchasingManager: "", // Not available
  };
}

/**
 * Checks if SKU interaction should be enabled based on available payment series data
 */
export function hasSkuPaymentSeries(
  commonOrderDetails: CommonOrderDetails
): boolean {
  return !!(
    commonOrderDetails.skuPaymentSeries &&
    commonOrderDetails.skuPaymentSeries.length > 0
  );
}

/**
 * Gets enhanced SKU data for chart display from payment series
 */
export function getEnhancedSkuData(
  sku: SKUItem,
  skuPaymentSeries?: SkuPaymentSeries[]
): {
  sku: string;
  itemName: string;
  cfroi: number;
  investment: number;
  currentStock: number;
  npv: number;
  paymentSeries: {
    b2cEntries: Array<{
      period: number;
      revenue: number;
      soldQuantity: number;
    }>;
    b2bEntries: Array<{
      period: number;
      revenue: number;
      soldQuantity: number;
    }> | null;
    totalRevenue: number;
    b2cTotalRevenue: number;
    b2bTotalRevenue: number;
  };
} | null {
  if (!skuPaymentSeries) {
    return null;
  }

  const skuPaymentData = skuPaymentSeries.find(
    (skuData) => skuData.position.sku === sku.skuId
  );

  if (!skuPaymentData) {
    return null;
  }

  const b2cTotal = skuPaymentData.paymentSeries.b2cEntries.reduce(
    (sum, entry) => sum + entry.revenue,
    0
  );
  const b2bTotal =
    skuPaymentData.paymentSeries.b2bEntries?.reduce(
      (sum, entry) => sum + entry.revenue,
      0
    ) || 0;
  const totalRevenue = b2cTotal + b2bTotal;

  return {
    sku: skuPaymentData.position.sku,
    itemName: sku.itemName,
    cfroi: skuPaymentData.cfroi,
    investment: skuPaymentData.investment,
    currentStock: skuPaymentData.currentStock,
    npv: skuPaymentData.npv,
    paymentSeries: {
      b2cEntries: skuPaymentData.paymentSeries.b2cEntries,
      b2bEntries: skuPaymentData.paymentSeries.b2bEntries,
      totalRevenue,
      b2cTotalRevenue: b2cTotal,
      b2bTotalRevenue: b2bTotal,
    },
  };
}
